// components/admin/EditDeviceParamsForm.tsx
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Form, Row, Col, Alert, Spinner, Button, Modal } from "react-bootstrap";
import http from "services/httpClient";

export interface Label {
  id: number;
  label: string;
  unit: string;
  column_index: number;
  is_enabled: boolean;
  multiplier: number;
  offset: number;
}

export interface EditDeviceParamsFormHandle {
  save: () => Promise<void>;
}

interface Props {
  deviceId: number;
}

interface TriggerEntry {
  id?: number;
  threshold: number;
  operator: string;
  trigger_count: number;
  reset_mode: number;
}

const MAX_TRIGGERS = 2;

const EMPTY_TRIGGER = (): TriggerEntry => ({
  threshold: 0,
  operator: ">",
  trigger_count: 1,
  reset_mode: 0,
});

const EditDeviceParamsForm = forwardRef<EditDeviceParamsFormHandle, Props>(
  ({ deviceId }, ref) => {
    const [labels, setLabels] = useState<Label[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showTriggerModal, setShowTriggerModal] = useState(false);
    const [triggerLabel, setTriggerLabel] = useState<Label | null>(null);
    const [triggerData, setTriggerData] = useState<TriggerEntry[]>([]);
    const [savingTriggers, setSavingTriggers] = useState(false);
    const [triggerError, setTriggerError] = useState<string | null>(null);

    const [deletedTriggerIds, setDeletedTriggerIds] = useState<number[]>([]);

    useEffect(() => {
      if (!deviceId) return;

      setLoading(true);
      http
        .get(`/device/${deviceId}/labels`)
        .then((res) => {
          setLabels(res.data);
          setError(null);
        })
        .catch(() => setError("Failed to load labels"))
        .finally(() => setLoading(false));
    }, [deviceId]);

    const updateLabel = <K extends keyof Label>(
      index: number,
      field: K,
      value: Label[K]
    ) => {
      const updated = [...labels];
      updated[index] = { ...updated[index], [field]: value };
      setLabels(updated);
    };

    const normalizeTriggers = (data: any): TriggerEntry[] => {
      if (!Array.isArray(data)) return [];

      return data.slice(0, MAX_TRIGGERS).map((item: any) => ({
        id: item.id,
        threshold: Number(item.threshold ?? 0),
        operator: item.operator ?? ">",
        trigger_count: Number(item.trigger_count ?? 1),
        reset_mode: Number(item.reset_mode ?? 0),
      }));
    };

    const handleOpenTrigger = async (label: Label) => {
      setTriggerLabel(label);
      setTriggerError(null);
      setTriggerData([]);
      setDeletedTriggerIds([]);

      try {
        const res = await http.get(
          `/triggers/?device_id=${deviceId}&column_index=${label.column_index}`
        );
        setTriggerData(normalizeTriggers(res.data));
      } catch (err: any) {
        if (err.response?.status === 404) {
          setTriggerData([]);
        } else {
          setTriggerError("Failed to load triggers");
          setTriggerData([]);
        }
      }

      setShowTriggerModal(true);
    };

    const updateTrigger = (
      index: number,
      field: keyof TriggerEntry,
      value: string | number
    ) => {
      setTriggerData((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                [field]: value,
              }
            : item
        )
      );
    };

    const addTrigger = () => {
      setTriggerData((prev) => {
        if (prev.length >= MAX_TRIGGERS) return prev;
        return [...prev, EMPTY_TRIGGER()];
      });
    };

    const removeTrigger = (index: number) => {
      setTriggerData((prev) => {
        const target = prev[index];

        if (target?.id) {
          setDeletedTriggerIds((ids) => [...ids, target.id!]);
        }

        return prev.filter((_, i) => i !== index);
      });
    };

    const normalizeThresholdKey = (value: number) => {
      if (!Number.isFinite(value)) return "NaN";
      return Number(value).toString();
    };

    const findDuplicateTrigger = (triggers: TriggerEntry[]) => {
      const seen = new Set<string>();

      for (const trigger of triggers) {
        const key = `${trigger.operator}__${normalizeThresholdKey(
          trigger.threshold
        )}`;

        if (seen.has(key)) {
          return trigger;
        }

        seen.add(key);
      }

      return null;
    };

    const handleSaveTrigger = async () => {
      if (!triggerLabel) return;

      setSavingTriggers(true);
      setTriggerError(null);

      try {
        const cleaned = triggerData.slice(0, MAX_TRIGGERS).map((trigger) => ({
          ...trigger,
          threshold: Number(trigger.threshold ?? 0),
          operator: trigger.operator,
          trigger_count: Math.max(
            1,
            Number(trigger.trigger_count ?? 1)
          ),
          reset_mode: Number(trigger.reset_mode ?? 0) === 1 ? 1 : 0,
        }));

        const duplicate = findDuplicateTrigger(cleaned);

        if (duplicate) {
          setTriggerError(
            `Duplicate trigger found: ${duplicate.operator} ${duplicate.threshold}`
          );
          setSavingTriggers(false);
          return;
        }

        for (const triggerId of deletedTriggerIds) {
          await http.delete(`/triggers/${triggerId}`);
        }

        for (const trigger of cleaned) {
          const payload = {
            device_id: deviceId,
            column_index: triggerLabel.column_index,
            threshold: trigger.threshold,
            operator: trigger.operator,
            trigger_count: trigger.trigger_count,
            reset_mode: trigger.reset_mode,
          };

          if (trigger.id) {
            await http.patch(`/triggers/${trigger.id}`, payload);
          } else {
            await http.post(`/triggers`, payload);
          }
        }

        setShowTriggerModal(false);
      } catch (err: any) {
        if (err.response?.status === 409) {
          setTriggerError("Duplicate trigger already exists");
        } else {
          setTriggerError("Failed to save triggers");
        }
      } finally {
        setSavingTriggers(false);
      }
    };

    useImperativeHandle(ref, () => ({
      async save() {
        try {
          await http.put(`/device/${deviceId}/labels`, labels);
        } catch {
          throw new Error("Failed to save labels");
        }
      },
    }));

    if (loading) return <Spinner animation="border" />;
    if (error) return <Alert variant="danger">{error}</Alert>;

    return (
      <>
        <Row className="mb-2 fw-bold text-muted">
          <Col md={1}>Enable</Col>
          <Col md={1}>Param</Col>
          <Col md={3}>Label</Col>
          <Col md={2}>Unit</Col>
          <Col md={2}>Multiplier</Col>
          <Col md={2}>Offset</Col>
          <Col md={1}>Trigger</Col>
        </Row>

        {labels.map((label, index) => (
          <Row key={label.id} className="mb-2">
            <Col md={1}>
              <Form.Check
                type="checkbox"
                checked={label.is_enabled}
                onChange={(e) =>
                  updateLabel(index, "is_enabled", e.target.checked)
                }
              />
            </Col>

            <Col md={1} className="d-flex align-items-center">
              x{label.column_index}
            </Col>

            <Col md={3}>
              <Form.Control
                type="text"
                value={label.label}
                onChange={(e) => updateLabel(index, "label", e.target.value)}
                placeholder={`Param ${label.column_index}`}
              />
            </Col>

            <Col md={2}>
              <Form.Control
                type="text"
                value={label.unit}
                onChange={(e) => updateLabel(index, "unit", e.target.value)}
                placeholder="Unit"
              />
            </Col>

            <Col md={2}>
              <Form.Control
                type="number"
                step="any"
                value={label.multiplier}
                onChange={(e) =>
                  updateLabel(
                    index,
                    "multiplier",
                    parseFloat(e.target.value) || 0
                  )
                }
              />
            </Col>

            <Col md={2}>
              <Form.Control
                type="number"
                step="any"
                value={label.offset}
                onChange={(e) =>
                  updateLabel(
                    index,
                    "offset",
                    parseFloat(e.target.value) || 0
                  )
                }
              />
            </Col>

            <Col md={1}>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => handleOpenTrigger(label)}
              >
                Trig
              </Button>
            </Col>
          </Row>
        ))}

        <Modal
          show={showTriggerModal}
          container={document.body}
          onHide={() => setShowTriggerModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Set Triggers for {triggerLabel?.label}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {triggerError && <Alert variant="danger">{triggerError}</Alert>}

            {triggerData.length === 0 ? (
              <div className="text-muted mb-3">
                No triggers configured for this parameter.
              </div>
            ) : (
              triggerData.map((trigger, index) => (
                <div
                  key={trigger.id ?? `new-${index}`}
                  className="border rounded p-3 mb-3"
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="fw-semibold">Alarm {index + 1}</div>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeTrigger(index)}
                    >
                      Delete
                    </Button>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Operator</Form.Label>

                    <Form.Select
                      value={trigger.operator}
                      onChange={(e) =>
                        updateTrigger(index, "operator", e.target.value)
                      }
                    >
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value=">=">&gt;=</option>
                      <option value="<=">&lt;=</option>
                      <option value="=">=</option>
                      <option value="!=">!=</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Threshold</Form.Label>

                    <Form.Control
                      type="number"
                      step="any"
                      value={trigger.threshold}
                      onChange={(e) =>
                        updateTrigger(
                          index,
                          "threshold",
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Consecutive Readings</Form.Label>

                        <Form.Control
                          type="number"
                          min={1}
                          step={1}
                          value={trigger.trigger_count}
                          onChange={(e) =>
                            updateTrigger(
                              index,
                              "trigger_count",
                              Math.max(
                                1,
                                parseInt(e.target.value, 10) || 1
                              )
                            )
                          }
                        />

                        <Form.Text className="text-muted">
                          Number of consecutive readings required.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reset Mode</Form.Label>

                        <Form.Select
                          value={trigger.reset_mode}
                          onChange={(e) =>
                            updateTrigger(
                              index,
                              "reset_mode",
                              Number(e.target.value)
                            )
                          }
                        >
                          <option value={0}>Auto Reset</option>
                          <option value={1}>Manual Reset</option>
                        </Form.Select>

                        <Form.Text className="text-muted">
                          {trigger.reset_mode === 0
                            ? "Resets when the value returns to normal."
                            : "Allows another trigger sequence while abnormal."}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))
            )}

            {triggerData.length < MAX_TRIGGERS && (
              <Button variant="outline-primary" onClick={addTrigger}>
                Add Trigger
              </Button>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowTriggerModal(false)}
              disabled={savingTriggers}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              onClick={handleSaveTrigger}
              disabled={savingTriggers}
            >
              {savingTriggers ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
);

export default EditDeviceParamsForm;